import bellSoundPath from '../assets/sounds/bell.mp3';

export type NotificationSoundMelody = 'bell' | 'double' | 'urgent' | 'soft' | 'custom';

export interface NotificationSoundSettings {
  melody: NotificationSoundMelody;
  volume: number;
  customSoundName?: string;
  customSoundDataUrl?: string;
}

export const notificationSoundOptions: { id: NotificationSoundMelody; name: string; description: string }[] = [
  { id: 'bell', name: 'Колокольчик', description: 'Стандартный сигнал' },
  { id: 'double', name: 'Двойной сигнал', description: 'Два коротких тона' },
  { id: 'urgent', name: 'Срочный', description: 'Более заметный сигнал' },
  { id: 'soft', name: 'Мягкий', description: 'Спокойное уведомление' },
  { id: 'custom', name: 'Свой файл', description: 'Звук, выбранный на этом компьютере' },
];

const STORAGE_KEY = 'telemedicine.notificationSoundSettings';
const MAX_CUSTOM_SOUND_BYTES = 2 * 1024 * 1024;

const normalizeVolumePercent = (value: number | undefined) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 100;
  return Math.max(0, Math.min(100, Math.round(value)));
};

const defaultSettings: NotificationSoundSettings = {
  melody: 'bell',
  volume: normalizeVolumePercent(window.__env?.settings?.volume),
};

const clampVolume = (value: number) => Math.max(0, Math.min(1, value));

const getVolume = (settings: NotificationSoundSettings) => {
  return clampVolume(normalizeVolumePercent(settings.volume) / 100);
};

const getAudioContext = () => {
  const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
  return AudioContextClass ? new AudioContextClass() : null;
};

const playAudioFile = async (src: string, settings: NotificationSoundSettings) => {
  const audio = new Audio(src);
  audio.volume = getVolume(settings);
  await audio.play();
};

const playTone = (context: AudioContext, frequency: number, startAt: number, duration: number, volume: number) => {
  const oscillator = context.createOscillator();
  const gain = context.createGain();

  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(frequency, startAt);
  gain.gain.setValueAtTime(0.0001, startAt);
  gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, volume), startAt + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start(startAt);
  oscillator.stop(startAt + duration + 0.03);
};

const playGeneratedMelody = async (
  melody: Exclude<NotificationSoundMelody, 'bell' | 'custom'>,
  settings: NotificationSoundSettings,
) => {
  const context = getAudioContext();
  if (!context) {
    await playAudioFile(bellSoundPath, settings);
    return;
  }

  if (context.state === 'suspended') {
    await context.resume();
  }

  const baseVolume = getVolume(settings) * 0.95;
  const now = context.currentTime;

  if (melody === 'double') {
    playTone(context, 880, now, 0.18, baseVolume);
    playTone(context, 1046, now + 0.24, 0.2, baseVolume);
  }

  if (melody === 'urgent') {
    playTone(context, 988, now, 0.16, baseVolume);
    playTone(context, 988, now + 0.22, 0.16, baseVolume);
    playTone(context, 1318, now + 0.44, 0.22, baseVolume);
  }

  if (melody === 'soft') {
    playTone(context, 659, now, 0.22, baseVolume);
    playTone(context, 784, now + 0.22, 0.24, baseVolume);
    playTone(context, 988, now + 0.46, 0.3, baseVolume);
  }
};

class NotificationSoundService {
  getSettings(): NotificationSoundSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return defaultSettings;

      const parsed = JSON.parse(raw) as NotificationSoundSettings;
      const supported = notificationSoundOptions.some((option) => option.id === parsed.melody);

      return supported
        ? { ...defaultSettings, ...parsed, volume: normalizeVolumePercent(parsed.volume ?? defaultSettings.volume) }
        : defaultSettings;
    } catch {
      return defaultSettings;
    }
  }

  saveSettings(settings: NotificationSoundSettings) {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...settings, volume: normalizeVolumePercent(settings.volume) }),
    );
  }

  async saveCustomSound(file: File): Promise<NotificationSoundSettings> {
    if (file.size > MAX_CUSTOM_SOUND_BYTES) {
      throw new Error('Файл звука должен быть не больше 2 МБ');
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

    const settings: NotificationSoundSettings = {
      ...this.getSettings(),
      melody: 'custom',
      customSoundName: file.name,
      customSoundDataUrl: dataUrl,
    };
    this.saveSettings(settings);

    return settings;
  }

  async play() {
    const settings = this.getSettings();

    try {
      if (settings.melody === 'custom' && settings.customSoundDataUrl) {
        await playAudioFile(settings.customSoundDataUrl, settings);
        return true;
      }

      if (settings.melody === 'bell' || settings.melody === 'custom') {
        await playAudioFile(bellSoundPath, settings);
        return true;
      }

      await playGeneratedMelody(settings.melody, settings);
      return true;
    } catch (error) {
      console.error('Error playing notification sound:', error);
      return false;
    }
  }
}

export const notificationSoundService = new NotificationSoundService();
